import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import Banner from "@/components/Banner";
import Breadcrumb from "@/components/Breadcrumb";
import EmptyState from "@/components/EmptyState";
import MetricCard from "@/components/MetricCard/MetricCard";
import PageHeading from "@/components/PageHeading/PageHeading";
import { TableHeading, type TableAction } from "@/components/Table";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import usePageTitle from "@/hooks/usePageTitle";
import { useParams } from "@/hooks/useSearchParams";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import AppLayout from "@/layouts/AppLayout";
import pluralize from "@/lib/pluralize";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { useNavigate } from "@/router";
import { useMembershipServicesNavLinks } from "../../_membershipServicesNavLinks";
import FinalizeElectionsDialog from "./_components/FinalizeElectionsDialog";
import MemberElectionsSearchInput from "./_components/MemberElectionsSearchInput";
import MemberElectionsTable from "./_components/MemberElectionsTable";
import UpdateElectionDateDialog, { type UpdateElectionDateDialogResult } from "./_components/UpdateElectionDateDialog";
import { useMemberElectionsList } from "./_hooks/useMemberElectionsList";
import { buildMemberElectionPersonPath } from "./_utils/buildMemberElectionPersonPath";
import {
  finalizeAllFailedMessage,
  finalizeListRefreshErrorMessage,
  finalizePartialFailureMessage
} from "./_utils/finalizeMemberElectionsFeedback";
import { selectedMemberElectionsClassState } from "./_utils/memberElectionClassification";
import { FINALIZE_MEMBER_ELECTIONS_MUTATION, UPDATE_MEMBERSHIP_APPLICATION_ELECTION_DATES_MUTATION } from "./mutations";

export default function MemberElectionsPage() {
  useRequireCapability({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageApplications
  });
  usePageTitle("/people/membership-services/member-elections", "People | Member Elections");
  const navigate = useNavigate();
  const { searchParamsUrl } = useParams();
  const navLinks = useMembershipServicesNavLinks();
  const bulkSelection = useBulkSelection();
  const { isSelectedOnly, deselectAll, rowSelectionCount, orderedSelectedIds, setRowSelection } = bulkSelection;
  const { Menu, setMenu } = useTableRowMenu();
  const [isFinalizeOpen, setIsFinalizeOpen] = useState(false);
  const [isUpdateDateOpen, setIsUpdateDateOpen] = useState(false);
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | undefined>(undefined);
  const [finalizeElections, { loading: finalizing }] = useMutation(FINALIZE_MEMBER_ELECTIONS_MUTATION);
  const [updateElectionDates, { loading: updatingElectionDates }] = useMutation(
    UPDATE_MEMBERSHIP_APPLICATION_ELECTION_DATES_MUTATION
  );

  const {
    searchValue,
    setSearchValue,
    clearSearch,
    searchActive,
    createHeaderPagination,
    createFooterPagination,
    loading,
    eligibleApplications,
    showLoadError,
    refetch,
    totalEligible,
    showMetricError,
    metricCardLoading,
    upcomingDates,
    hasUpcomingDates
  } = useMemberElectionsList({ deselectAll });

  const goToUpcomingDates = () => navigate("/people/membership-services/member-elections/upcoming-dates");
  const { missingClassCount, cannotFinalize } = useMemo(
    () => selectedMemberElectionsClassState(eligibleApplications, orderedSelectedIds),
    [eligibleApplications, orderedSelectedIds]
  );
  const openFinalizeDialog = useCallback(() => {
    if (rowSelectionCount === 0) return;
    setIsFinalizeOpen(true);
  }, [rowSelectionCount]);
  const closeFinalizeDialog = useCallback(() => {
    if (finalizing) return;
    setIsFinalizeOpen(false);
  }, [finalizing]);
  const confirmFinalize = useCallback(async () => {
    if (orderedSelectedIds.length === 0 || finalizing || cannotFinalize) return;

    try {
      const { data } = await finalizeElections({ variables: { applicationIds: orderedSelectedIds } });
      const result = data?.finalizeMemberElections;
      if (!result?.success) {
        showErrorSnackbar(result?.message ?? "Unable to finalize elections");
        return;
      }

      const failedIds = result.failedApplicationIds ?? [];
      const successfulCount = result.successfulApplicationIds?.length ?? 0;
      if (failedIds.length > 0) {
        showErrorSnackbar(
          successfulCount > 0 ? finalizePartialFailureMessage(successfulCount) : finalizeAllFailedMessage()
        );
        setRowSelection(Object.fromEntries(failedIds.map((id) => [id, true])));
      } else {
        const personLabel = pluralize(successfulCount, "person", undefined, "people");
        showSnackbar(`Finalized elections for ${successfulCount} ${personLabel}.`, { close: true });
        deselectAll();
      }
      setIsFinalizeOpen(false);
      try {
        await refetch();
      } catch {
        showErrorSnackbar(finalizeListRefreshErrorMessage(successfulCount));
      }
    } catch (error) {
      showErrorSnackbar((error as Error).message);
    }
  }, [cannotFinalize, deselectAll, finalizeElections, finalizing, orderedSelectedIds, refetch, setRowSelection]);
  const openUpdateDateDialog = useCallback(() => {
    if (rowSelectionCount === 0) return;
    setIsUpdateDateOpen(true);
  }, [rowSelectionCount]);
  const closeUpdateDateDialog = useCallback(() => setIsUpdateDateOpen(false), []);
  const confirmUpdateDate = useCallback(
    async (result: UpdateElectionDateDialogResult) => {
      try {
        const { data } = await updateElectionDates({
          variables: {
            input: {
              applicationIds: orderedSelectedIds,
              electionDateId: result.action === "assign" ? result.electionDateId : null
            }
          }
        });

        if (data?.updateMembershipApplicationElectionDates.success) {
          const message = data.updateMembershipApplicationElectionDates.message;
          showSnackbar(message);
          setIsUpdateDateOpen(false);
          deselectAll();
          try {
            await refetch();
          } catch {
            showErrorSnackbar("Election date changes were saved, but the list could not be refreshed.");
          }
        } else {
          showErrorSnackbar(
            data?.updateMembershipApplicationElectionDates.message ?? "Unable to update election dates"
          );
        }
      } catch (error) {
        showErrorSnackbar((error as Error).message);
      }
    },
    [updateElectionDates, orderedSelectedIds, deselectAll, refetch]
  );

  useEffect(() => {
    if (rowSelectionCount === 0 && isSelectedOnly) {
      deselectAll();
    }
  }, [rowSelectionCount, isSelectedOnly, deselectAll]);

  const bulkActions = useMemo<TableAction[]>(
    () => [
      {
        label: "Update Election Date",
        variant: "outlined",
        onClick: openUpdateDateDialog,
        testId: "update-election-date-button"
      },
      {
        label: "Finalize Elections",
        variant: "outlined",
        onClick: openFinalizeDialog,
        testId: "finalize-elections-button"
      }
    ],
    [openFinalizeDialog, openUpdateDateDialog]
  );

  const openPersonSideSheet = useCallback((pgaId: string) => {
    setSelectedPgaId(pgaId);
    setIsPersonSideSheetOpen(true);
  }, []);

  const closePersonSideSheet = useCallback(() => {
    setIsPersonSideSheetOpen(false);
    setSelectedPgaId(undefined);
  }, []);

  return (
    <AppLayout interior bottomBar navLinks={navLinks}>
      <Menu />
      <Breadcrumb
        title="Operations"
        compactTitle="Member Election Management"
        backLink={{ to: "/people/membership-services" }}
        compact
      />
      <PageHeading title="Membership Elections" compact />
      <div className="w-full">
        {!hasUpcomingDates && (
          <div className="mt-6">
            <Banner
              variant="warn"
              description="There are no upcoming member election dates, which prevents automatic election of eligible people"
              primaryAction={{ label: "Add Dates", onClick: goToUpcomingDates }}
              hideSecondaryDismiss
            />
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            iconName="person"
            label="Total Eligible for Election in Next Cycle"
            value={showMetricError ? null : String(totalEligible)}
            loading={metricCardLoading}
            variant={showMetricError ? "error" : undefined}
            tooltip={{
              supportingText: showMetricError
                ? "Unable to load the eligible count. Try refreshing the page."
                : "The number of people currently eligible for election whose election will be processed on the next election date."
            }}
          />
          <MetricCard iconName="person" label="Total Eligible with Election Date in Future Cycle" value="0" />
        </div>

        <TableHeading
          heading="Eligible for Member Election"
          body={
            "View all people who are eligible for election to membership, which will be automatically handled on the corresponding election date. " +
            "You can also manually process elections earlier by selecting the applicable individuals and pressing \u201CFinalize Elections\u201D. " +
            "To postpone one or more elections, select the individuals via the checkboxes below and change or remove their election date. " +
            "They will remain in this table as long as they are still eligible for election."
          }
          actions={[
            {
              label: "Edit Upcoming Dates",
              variant: "outlined",
              onClick: goToUpcomingDates
            }
          ]}
        />

        {showLoadError ? (
          <>
            <div className="mb-4 max-w-[320px]">
              <MemberElectionsSearchInput query={searchValue} onChange={setSearchValue} onClear={clearSearch} />
            </div>
            <EmptyState
              iconName="error"
              title="Error"
              caption="An error occurred while loading people eligible for member election."
              action={{ children: "Try Again", onClick: () => refetch() }}
            />
          </>
        ) : (
          <MemberElectionsTable
            loading={loading}
            applications={eligibleApplications}
            createHeaderPagination={createHeaderPagination}
            createFooterPagination={createFooterPagination}
            bulkSelection={bulkSelection}
            bulkActions={bulkActions}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            clearSearch={clearSearch}
            hasActiveSearch={searchActive}
            setMenu={setMenu}
            onViewPerson={openPersonSideSheet}
          />
        )}
        <PersonSideSheet
          open={isPersonSideSheetOpen}
          onClose={closePersonSideSheet}
          pgaId={selectedPgaId ?? null}
          showEmployment={true}
          showFacility={true}
          customActionButtons={
            selectedPgaId
              ? [
                  {
                    text: "View Person",
                    variant: "outlined" as const,
                    onClick: () => {
                      const personPath = buildMemberElectionPersonPath(selectedPgaId, searchParamsUrl);
                      if (personPath) {
                        navigate(personPath);
                      }
                    }
                  }
                ]
              : undefined
          }
        />
        <FinalizeElectionsDialog
          open={isFinalizeOpen}
          selectedCount={rowSelectionCount}
          missingClassCount={missingClassCount}
          cannotFinalize={cannotFinalize}
          confirming={finalizing}
          onClose={closeFinalizeDialog}
          onConfirm={confirmFinalize}
        />
        <UpdateElectionDateDialog
          open={isUpdateDateOpen}
          selectedCount={rowSelectionCount}
          upcomingDates={upcomingDates}
          saving={updatingElectionDates}
          onClose={closeUpdateDateDialog}
          onConfirm={confirmUpdateDate}
        />
      </div>
    </AppLayout>
  );
}
